import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { ISODate } from '@app/shared'
import clsx from 'clsx'

import {
  useDesktopWorker,
  useJournalEntry,
  useSaveJournalEntry,
  useVoiceMemos,
  type JournalVoiceMemo,
} from '../../lib/data'
import { clockFromTimestamp, durationPhrase } from '../../lib/time'
// The durable draft (JRN-1a/b), now one hook shared by every composer in the app — see
// lib/use-journal-draft.ts for the rules it keeps and lib/journal-draft.ts for the store.
import { unsavedSentence, useJournalDraft } from '../../lib/use-journal-draft'
import { useHold } from '../lib/useHold'
import { MJournal, MMic } from '../shell/icons'

// ============================================================================
// 1a · THE COMPOSER CARD — docs/design/handoff-mobile/README.md §1a.
//
// "Write today's entry. Everything else on the screen exists to inform that
// writing." It is roughly 60% of the visible column and it is always first —
// which is the entire argument for the mobile app existing.
//
// ── THE UNDO CONTRACT. No gate can see this, so it is written down here, in the
// same words the desktop composer carries.
//
// The textarea is UNCONTROLLED (defaultValue, never value). A controlled input
// whose value is re-set by an autosave round trip silently destroys the browser's
// native undo stack after the first save, and no screenshot and no assertion can
// photograph a broken undo. The `key` makes React remount it when the DATE
// changes, which is the only time the text should be replaced from outside.
//
// If you ever change this to a controlled input, check BY HAND on a phone: type,
// wait for the save chip, then long-press the field and choose Undo.
//
// ── THE KEYSTROKE PROMISE. The debounce is a window in which the words exist
// only in the DOM. On a phone that window closes constantly — a call arrives, the
// app backgrounds, the OS reclaims the tab — so `visibilitychange` and `pagehide`
// both flush it. `pagehide` rather than `beforeunload`, which Android never fires.
// ============================================================================

type SaveState = 'idle' | 'saving' | 'saved' | 'failed'

/** Long enough not to send a request per word, short enough to lose nothing on the way out. */
const AUTOSAVE_MS = 800

/**
 * The prompt whisper the mockup draws when the day carries no prompt of its own.
 *
 * It is UI copy, not a claim: "A suggestion, never a required field" (README
 * §1a). When the entry HAS a prompt — the rotating pool the journal domain owns —
 * that one is shown instead, because the day's real prompt outranks the standing
 * nudge and showing both would be two questions on one card.
 */
const WHISPER = 'What’s taking up mental space right now?'

export function Composer({
  date,
  nowMinutes,
  label = 'Today’s entry',
  onRecord,
  onRecordRelease,
}: {
  date: ISODate
  nowMinutes: number
  /**
   * The eyebrow, and the only thing on this card that knows which day it is looking at. A PAST
   * day says its own date here — "Today's entry" over Tuesday would be the card claiming to be
   * a screen it is not (JRN-1c).
   */
  label?: string
  /**
   * The 56px circle was held down. Same recording surface the `+` opens.
   *
   * BOTH OPTIONAL, and their absence is the whole switch: a voice memo is a TODAY thing (it is
   * recorded now, queued now, and transcribed by a worker that is running now), so a past day
   * gets no record row at all rather than a button that would file this morning's audio under
   * last Tuesday. Leaving them out also leaves the memo READS out — see `RecordRow`, which owns
   * those hooks so they only run on the day that has a recorder.
   */
  onRecord?: () => void
  onRecordRelease?: () => void
}) {
  const { data: entry, isPending, error: readError, refetch } = useJournalEntry(date)
  const save = useSaveJournalEntry(date)
  /** Today is the only day that records. Everything memo-shaped hangs off this. */
  const records = onRecord !== undefined && onRecordRelease !== undefined

  const [words, setWords] = useState(0)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** The write waiting on the debounce. It carries its own date: a flush can land after midnight. */
  const pending = useRef<{ date: ISODate; text: string } | null>(null)

  // ── THE DURABLE DRAFT ─────────────────────────────────────────
  //
  // Every rule about where unsaved words live is in lib/use-journal-draft.ts,
  // shared with the desktop card and the desktop day page, so the three cannot
  // drift about what "not saved" means. What stays here is this card's own
  // debounce, its flush on the way out, and its pixels.
  const draft = useJournalDraft({
    date,
    serverText: entry?.text,
    readFailed: Boolean(readError),
    settled: !isPending,
    busy: save.isPending || pending.current !== null,
    send: (text) => commit(text),
  })
  const { restored, unconfirmed, kept, autoRetries, wouldReplace } = draft
  /** The box is ahead of the server, read during render so the word-count effect can see it. */
  const ahead = useRef(false)
  ahead.current = unconfirmed

  useEffect(() => {
    // Held back while a draft is in the box: the server's count belongs to the server's text, and
    // a count that disagrees with the words on screen is the app claiming to know something it
    // does not.
    if (entry && !ahead.current) setWords(entry.wordCount)
  }, [entry])

  const flushRef = useRef<() => void>(() => {})
  flushRef.current = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const write = pending.current
    if (!write) return
    pending.current = null
    // NEITHER CALLBACK BELOW RUNS WHEN THIS IS THE UNMOUNT FLUSH — TanStack v5 drops
    // `mutate`-level callbacks for an unmounted observer, which is why a failed flush used to
    // restore the old text in silence. They are UI only now: the draft that makes the words
    // survivable is written on the keystroke and cleared by the mutation itself.
    save.mutate(write, {
      onSuccess: () => setSaveState('saved'),
      onError: () => setSaveState('failed'),
    })
  }

  useEffect(() => {
    const flush = () => flushRef.current()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', flush)
      flush() // leaving the tab, changing the day — both count as leaving
    }
  }, [])

  /**
   * ONE SAVE, WITH THE CHIP TOLD THE TRUTH AFTERWARDS.
   *
   * The draft is NOT cleared here — `useSaveJournalEntry` clears it, because
   * this callback does not run when the save was fired by an unmount. All this
   * does is re-read the store and let the chip say what the store says.
   */
  function commit(text: string) {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    pending.current = null
    setSaveState('saving')
    save.mutate(
      { date, text },
      {
        onSuccess: (saved) => {
          draft.noteSaved(saved.text)
          setSaveState('saved')
        },
        // The text is still in the textarea — it is uncontrolled, so a failed save cannot erase
        // it — and now it is on the phone's disk as well. What must not happen is the card
        // claiming "Saved" when nothing was.
        onError: () => setSaveState('failed'),
      },
    )
  }

  // The restored words are the ones on screen, so they are the ones counted.
  useEffect(() => {
    if (restored) setWords(restored.text.trim() ? restored.text.trim().split(/\s+/).length : 0)
  }, [restored])

  /**
   * THE CARD GROWS WITH THE ENTRY. A fixed-height textarea on a phone hides the
   * end of what you just wrote behind an inner scrollbar, which is the app
   * quietly disagreeing with "the composer is the page".
   *
   * It touches `style.height` and nothing else — the VALUE is still the DOM's,
   * so the undo contract is untouched. `auto` first, because scrollHeight never
   * shrinks on its own.
   */
  const field = useRef<HTMLTextAreaElement>(null)
  function grow() {
    const node = field.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }
  useEffect(grow, [entry?.text, restored?.text])

  function onType(text: string) {
    grow()
    setWords(text.trim() ? text.trim().split(/\s+/).length : 0)

    // DURABLE FIRST, AND SYNCHRONOUSLY — before the debounce, before the request, before
    // anything that can be interrupted. Everything below this line can be lost: the timer, the
    // fetch, the tab, the process. This line cannot.
    draft.noteKeystroke(text)

    // DRAFT-ONLY MODE. Today's entry could not be read, so there is nothing to write onto: a PUT
    // now would replace whatever is actually saved with a box that started empty. The words are
    // kept on this phone and the send waits — the notice under the field says exactly that.
    if (readError) {
      setSaveState('idle')
      return
    }

    setSaveState('saving')
    pending.current = { date, text }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      pending.current = null
      commit(text)
    }, AUTOSAVE_MS)
  }

  return (
    // 12d, TEXT ENTRY: the whole card is the field, the same choice
    // components/today/JournalEntryCard.tsx makes for the desktop composer —
    // the textarea is the only thing worth focusing on this card, so the ring
    // belongs to the card (`:focus-within`), not to the textarea alone. The
    // resting look is untouched: `--mo-rest-shadow` carries the existing
    // `shadow-card` through so hover/focus never drop it.
    <section
      style={{ '--mo-rest-shadow': 'var(--shadow-card)' } as CSSProperties}
      className="mo-field-journal flex flex-none flex-col gap-[10px] rounded-context border border-line bg-surface px-4 pb-3 pt-[14px] shadow-card"
    >
      {/* 1 · the eyebrow row */}
      <div className="flex items-center gap-[9px]">
        <MJournal className="flex-none text-journal" />
        <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-muted">
          {label}
        </span>
        <SaveChip
          state={saveState}
          unconfirmed={unconfirmed}
          kept={kept}
          autoRetries={autoRetries}
          lastSavedAt={entry && entry.text.length > 0 ? entry.updatedAt : null}
          date={date}
          nowMinutes={nowMinutes}
        />
      </div>

      {/* 2 · the prompt whisper. The standing nudge is a question about RIGHT NOW, so it is
          offered on today only; a past day shows the prompt it was actually written under, or
          nothing at all rather than a question it is too late to answer. */}
      {(entry?.prompt || records) && (
        <p className="text-[12.5px] font-normal leading-[1.45] text-microcopy text-pretty">
          {entry?.prompt || WHISPER}
        </p>
      )}

      {/* 3 · the entry itself.
          A FAILED READ MUST NOT LOOK LIKE AN EMPTY DAY: an empty textarea says
          "you wrote nothing", and sending the first keystroke would then
          overwrite whatever is actually on the server with a blank.

          THE FIELD STAYS OPEN ANYWAY (JRN-1a). Writing is what this app is for,
          and refusing the keystroke is the one failure the owner cannot work
          around on a phone. So the read failure holds the SEND, not the typing:
          the words go to this phone's disk on every keystroke and are put back
          in the box, with a way to send them, as soon as the entry can be read.
          That is a promise the code keeps below, not a reassurance. */}
      {readError && (
        <div className="rounded-row border border-danger-line bg-danger-fill px-3 py-[10px]">
          <p className="text-[12.5px] leading-[1.5] text-danger text-pretty">
            Today’s entry could not be loaded, so what is already saved is not shown and nothing
            is sent yet — sending now would overwrite it. Keep writing: the words are kept on this
            phone, and when the entry can be read they are put back here with a way to send them.{' '}
            {readError instanceof Error ? readError.message : ''}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 h-11 rounded-tile bg-danger-solid px-4 text-[13.5px] font-semibold leading-none text-on-dark active:bg-danger-solid-2"
          >
            Try again
          </button>
        </div>
      )}

      <textarea
        // The key changes exactly twice: when the DATE changes, and the one time
        // a restored draft is put back. Never again after that — see the undo
        // contract at the top of this file.
        key={draft.fieldKey}
        ref={field}
        defaultValue={restored?.text ?? entry?.text ?? ''}
        onChange={(e) => onType(e.target.value)}
        // A failed READ does not disable the field — it only holds the send.
        disabled={isPending}
        placeholder="Write as much or as little as you want. Nothing here is graded."
        aria-label={records ? 'Today’s journal entry' : `Journal entry for ${date}`}
        rows={5}
        className="min-h-[124px] w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-[15px] font-normal leading-[1.62] text-ink outline-none placeholder:text-microcopy text-pretty"
      />

      {/* 3b · THE WORDS THIS PHONE KEPT. Shown only when a draft was put back and
          the server has still not confirmed it — one sentence about where the
          words are, and one button that sends them. */}
      {restored && unconfirmed && !readError && (
        <div className="rounded-row border border-warning-line bg-warning-fill px-3 py-[10px]">
          <p className="text-[12.5px] leading-[1.5] text-warning-2 text-pretty">
            Not saved. These words were kept on this phone
            {restored.typedAt.includes('T') ? ` from ${clockFromTimestamp(restored.typedAt)}` : ''} and
            put back here.{' '}
            {wouldReplace
              ? 'Today’s saved entry is different from them, so sending replaces what is saved — Retry is the only thing that sends them.'
              : 'Sending them keeps everything that is already saved, and they go up on their own once you are back online.'}
          </p>
          <button
            type="button"
            onClick={() => commit(field.current?.value ?? restored.text)}
            className="mt-2 h-11 rounded-tile bg-ink px-4 text-[13.5px] font-semibold leading-none text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]"
          >
            Retry
          </button>
        </div>
      )}

      {/* 4 · the metadata line */}
      <div className="flex items-center gap-[9px] text-[11.5px] font-medium leading-none text-muted">
        <span>
          {words} {words === 1 ? 'word' : 'words'}
        </span>
        {(entry?.tags.length ?? 0) > 0 && (
          <>
            <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
            <span className="truncate">{entry!.tags.map((t) => `#${t}`).join(' · ')}</span>
          </>
        )}
      </div>

      {/* 5 · the divider and 6/7 · the recorder — TODAY ONLY. */}
      {records && <RecordRow date={date} onRecord={onRecord} onRecordRelease={onRecordRelease} />}
    </section>
  )
}

/**
 * THE RECORD ROW AND THE MEMOS UNDER IT — its own component so that a past day,
 * which has no recorder, also issues no memo reads. Hooks cannot be called
 * conditionally; a component can simply not be rendered, and that is the whole
 * reason this is a component at all.
 */
function RecordRow({
  date,
  onRecord,
  onRecordRelease,
}: {
  date: ISODate
  onRecord: () => void
  onRecordRelease: () => void
}) {
  const { data: memos } = useVoiceMemos(date)
  const { data: worker } = useDesktopWorker()
  const hold = useHold({
    // A tap is not a recording. The label says "Hold to record", and a 90ms tap
    // producing a one-second memo would be the control doing something nobody
    // asked for.
    onTap: () => {},
    onHold: onRecord,
    onRelease: onRecordRelease,
  })

  const queued = (memos ?? []).filter((m) => m.status === 'queued' || m.status === 'recording')
  // README §1a: "A queued memo always names the worker it is waiting on." The
  // name is the API's (`/api/worker-status` → `machine`), never the mockup's
  // fixture. React Query dedupes this against the recording surface's own read,
  // so it is one request however many rows are on screen.
  const workerName = worker?.machine ?? null

  return (
    <>
      <div className="h-px bg-fill" aria-hidden />

      <div className="flex items-center gap-[13px]">
        <button
          type="button"
          aria-label="Hold to record a voice memo"
          {...hold}
          // `text-surface` on `bg-ink`: the tile inverts with the theme and the
          // glyph must invert with it (see mobile/shell/parts.tsx → Chip).
          className="flex size-[56px] flex-none touch-none select-none items-center justify-center rounded-full bg-ink text-surface shadow-mic active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]"
        >
          <MMic strokeWidth={1.8} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold leading-[1.2] text-ink">Hold to record</div>
          <div className="mt-1 text-[11.5px] font-normal leading-[1.45] text-muted text-pretty">
            {memoSummary(memos)}
          </div>
        </div>
      </div>

      {queued.map((memo) => (
        <QueuedMemoRow key={memo.id} memo={memo} worker={workerName} />
      ))}
    </>
  )
}

/**
 * 'Two today — 7:12am memo transcribed into this entry.'
 *
 * Every part is counted off the memos that actually exist. A day with none says
 * so plainly rather than borrowing the mockup's sentence about two.
 */
function memoSummary(memos: JournalVoiceMemo[] | undefined): string {
  if (!memos || memos.length === 0) return 'Nothing recorded today yet.'
  const transcribed = memos.filter((m) => m.status === 'transcribed')
  const count = memos.length
  const word = count === 1 ? 'One' : count === 2 ? 'Two' : String(count)
  const noun = count === 1 ? 'memo' : 'memos'
  const first = transcribed[0]
  if (!first) return `${word} ${noun} today — none transcribed yet.`
  return `${word} today — ${clockFromTimestamp(first.recordedAt)} memo transcribed into this entry.`
}

/** The `warning` set, a 5-bar waveform, the stamp, and a QUEUED pill. */
function QueuedMemoRow({ memo, worker }: { memo: JournalVoiceMemo; worker: string | null }) {
  const recording = memo.status === 'recording'
  return (
    <div className="flex items-center gap-[10px] rounded-row border border-warning-line bg-warning-fill px-[11px] py-[9px]">
      <div className="flex h-5 flex-none items-center gap-[2px]" aria-hidden>
        {[9, 16, 11, 18, 7].map((h, i) => (
          <span
            key={i}
            className="w-[2px] rounded-[2px] bg-warning-line"
            style={{ height: `${h}px` }}
          />
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium leading-[1.2] text-warning">
          {clockFromTimestamp(memo.recordedAt)} · {durationPhrase(memo.durationSec)}
        </div>
        <div className="mt-1 text-[10.5px] font-normal leading-[1.35] text-warning-2">
          {recording
            ? 'Recording now'
            : worker
              ? `Queued — waiting on ${worker}`
              : // The worker has never checked in with a name, so none is
                // invented. What it is waiting for is still stated.
                'Queued — waiting on your PC, which has not checked in with a name'}
        </div>
      </div>
      <span className="flex-none rounded-pill bg-warning-fill px-2 py-[3px] text-[9.5px] font-semibold uppercase leading-[1.4] tracking-[0.05em] text-warning-2">
        {recording ? 'Recording' : 'Queued'}
      </span>
    </div>
  )
}

/**
 * The chip never claims more than it knows. At rest it reports when the entry was
 * last written from the entry's own `updatedAt` — a relative phrase inside an
 * hour, the clock time otherwise, and nothing at all for a day never written.
 */
function SaveChip({
  state,
  unconfirmed,
  kept,
  autoRetries,
  lastSavedAt,
  date,
  nowMinutes,
}: {
  state: SaveState
  /**
   * A draft is on this phone that the server has not confirmed. It OUTRANKS
   * 'saved' and the idle stamp, and that is the fix: `lastSavedAt` comes from
   * `entry.updatedAt`, which the optimistic write in useSaveJournalEntry sets
   * BEFORE the request — so "Saved just now" could render over words the server
   * never accepted. The store is the only thing that knows, so the store wins.
   */
  unconfirmed: boolean
  /** The draft write actually landed. False in a browser that refuses storage. */
  kept: boolean
  /** Something will send these words without the owner touching anything. */
  autoRetries: boolean
  lastSavedAt: string | null
  date: ISODate
  nowMinutes: number
}) {
  // ONE SENTENCE FOR EVERY WAY THE WORDS ARE NOT SAVED, and it promises only what is wired:
  // the retry line appears exactly when the `online`/visible resend would actually fire, and
  // the phone is only credited with keeping the words when it did.
  if (unconfirmed || state === 'failed') {
    if (state === 'saving') return <Pill tone="quiet">Saving…</Pill>
    return <Pill tone="bad">{unsavedSentence(kept, autoRetries, 'on this phone')}</Pill>
  }

  if (state === 'idle') {
    if (!lastSavedAt) return null
    const sameDay = lastSavedAt.slice(0, 10) === date
    const savedMinutes = Number(lastSavedAt.slice(11, 13)) * 60 + Number(lastSavedAt.slice(14, 16))
    const ago = nowMinutes - savedMinutes
    const recent = sameDay && ago >= 0 && ago < 60
    return (
      <Pill tone="ok">
        {recent
          ? ago < 1
            ? 'Saved just now'
            : `Saved ${ago}m ago`
          : `Saved ${clockFromTimestamp(lastSavedAt)}`}
      </Pill>
    )
  }

  return (
    <Pill tone={state === 'saved' ? 'ok' : 'quiet'}>
      {state === 'saving' && 'Saving…'}
      {state === 'saved' && 'Saved just now'}
    </Pill>
  )
}

function Pill({ tone, children }: { tone: 'ok' | 'bad' | 'quiet'; children: React.ReactNode }) {
  return (
    <span
      aria-live="polite"
      className={clsx(
        'ml-auto flex-none rounded-pill px-2 py-[3px] text-[10px] font-semibold leading-[1.4]',
        tone === 'ok' && 'bg-success-fill text-success',
        tone === 'bad' && 'bg-danger-fill text-danger-2',
        tone === 'quiet' && 'bg-fill text-muted',
      )}
    >
      {children}
    </span>
  )
}
